/**
 * Sidebar customization state helpers for AppShell.
 * Pure functions for group ordering, item draft application,
 * and sidebar preferences persistence logic.
 * No React hooks — these are stateless utilities called from AppShell.
 */
import { resolveGroupKey, resolveItemKey } from './menuMerge'
import type { SidebarGroup, SidebarItem } from './menuMerge'
import type { SidebarCustomizationDraft } from './appShellTypes'
import type { AppShellProps } from './appShellTypes'

export function mergeGroupOrder(preferred: string[], current: string[]): string[] {
  const seen = new Set<string>()
  const merged: string[] = []
  for (const id of preferred) {
    const trimmed = id.trim()
    if (!trimmed || seen.has(trimmed) || !current.includes(trimmed)) continue
    seen.add(trimmed)
    merged.push(trimmed)
  }
  for (const id of current) {
    if (seen.has(id)) continue
    seen.add(id)
    merged.push(id)
  }
  return merged
}

export function collectSidebarDefaults(groups: SidebarGroup[]) {
  const groupDefaults = new Map<string, string>()
  const itemDefaults = new Map<string, string>()

  const visitItems = (items: SidebarItem[]) => {
    for (const item of items) {
      const key = resolveItemKey(item)
      const baseTitle = item.defaultTitle ?? item.title
      itemDefaults.set(key, baseTitle)
      itemDefaults.set(item.href, baseTitle)
      if (item.children && item.children.length > 0) visitItems(item.children)
    }
  }

  for (const group of groups) {
    const key = resolveGroupKey(group)
    groupDefaults.set(key, group.defaultName ?? group.name)
    visitItems(group.items)
  }

  return { groupDefaults, itemDefaults }
}

export function applyItemDraft(item: SidebarItem, draft: SidebarCustomizationDraft): SidebarItem {
  const itemKey = resolveItemKey(item)
  const baseTitle = item.defaultTitle ?? item.title
  const override = draft.itemLabels[itemKey]?.trim()
  const children = item.children
    ? item.children.map((child) => applyItemDraft(child, draft))
    : undefined
  const hidden = draft.hiddenItemIds[itemKey] === true
  return {
    ...item,
    title: override && override.length > 0 ? override : baseTitle,
    hidden,
    children,
  }
}

export function applyCustomizationDraft(
  baseGroups: SidebarGroup[],
  draft: SidebarCustomizationDraft,
  cloneGroups: (groups: AppShellProps['groups']) => AppShellProps['groups'],
): SidebarGroup[] {
  const clones = cloneGroups(baseGroups) as SidebarGroup[]
  const byId = new Map<string, SidebarGroup>()
  for (const group of clones) {
    byId.set(resolveGroupKey(group), group)
  }
  const orderedIds = mergeGroupOrder(draft.order, Array.from(byId.keys()))
  const seen = new Set<string>()
  const result: SidebarGroup[] = []
  for (const id of orderedIds) {
    if (seen.has(id)) continue
    const group = byId.get(id)
    if (!group) continue
    seen.add(id)
    const baseName = group.defaultName ?? group.name
    const override = draft.groupLabels[id]?.trim()
    result.push({
      ...group,
      name: override && override.length > 0 ? override : baseName,
      items: group.items.map((item) => applyItemDraft(item, draft)),
    })
  }
  return result
}

export function filterMainSidebarGroups(groups: SidebarGroup[]): SidebarGroup[] {
  const isMainItem = (item: SidebarItem): boolean => {
    if (item.pageContext && item.pageContext !== 'main') return false
    return true
  }

  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter(isMainItem).map((item) => ({
        ...item,
        children: item.children?.filter(isMainItem),
      })),
    }))
    .filter((group) => group.items.length > 0)
}
